import React, { memo, useEffect, useMemo, useState, useCallback, useRef } from 'react';
import { useSelector } from 'react-redux';
import { useFieldArray, useFormContext } from 'react-hook-form';
import {
  useReactTable,
  getCoreRowModel,
  getExpandedRowModel,
  ExpandedState,
} from '@tanstack/react-table';
import { Icon } from '@tcc/crate';
import { Table } from '@tcc/fds-table';
import { Button, ButtonFill, ComponentSize } from '@tcc/fds';
import { useWizard } from 'react-use-wizard';
import { inferUnit } from 'util/unitConversion';
import { getMessage } from 'util/locale';
import { getBagSizeForCrop } from 'user/selectors';
import {
  HybridDropdownCell,
  PopulationInputCell,
  SeedCostInputCell,
  TargetYieldInputCell,
  ActionsCell,
} from '../CustomCells';
import ReviewPlan from '../ReviewPlan';
import { FieldData, FormData } from '../../types';
import { Product } from 'store/types/types';
import { ErrorHandler } from './ErrorHandler';
import { GenerateAllButton } from './GenerateAllButton';

interface FieldsTableProps {
  currency: string;
  multipleHybrids?: boolean;
  onFieldsLoad: (fields: FieldData[]) => void;
  excludedBrandUuids?: string[];
}

export const FieldsTable = memo(
  function FieldsTable({ currency, multipleHybrids, onFieldsLoad, excludedBrandUuids }: FieldsTableProps) {
    const bagSize = useSelector((state) => getBagSizeForCrop(state, 'corn'));
    const { control, getValues, setValue } = useFormContext<FormData>();
    const { previousStep } = useWizard();
    const [expanded, setExpanded] = useState<ExpandedState>({});
    const generateHandlersRef = useRef(new Map<string, () => void>());

    const { fields, remove, insert, update } = useFieldArray<FormData, 'fields', '_id'>({
      control,
      name: 'fields',
      // useFieldArray overwrites the id (field id) from the data. To avoid that, we set a custom keyName
      keyName: '_id',
    });

    const fieldsWithIndex = useMemo(
      () => fields.map((field, index) => ({ ...field, _index: index })),
      [fields]
    );

    const [usedHybrids, setUsedHybrids] = useState<Record<string, Product[]>>(
      () =>
        fieldsWithIndex.reduce((acc, field) => {
          if (field?.hybrid) {
            const countryCode = field.countryCode;
            if (!acc[countryCode]) {
              acc[countryCode] = [];
            }
            acc[countryCode].push(field.hybrid);
          }
          return acc;
        }, {} as Record<string, Product[]>)
    );

    const handleRemove = useCallback((index: number) => {
      if (fields.length === 1) {
        remove(index);
        previousStep();
      } else {
        remove(index);
      }
    }, [fields.length, remove, previousStep]);

    const renderReviewPlan = useCallback((parentRowData) => {
      // TODO: Pass multipleHybrids to ReviewPlan component when the multiple hibrids functionality is enabled
      // behind a flag for advisors. Multiple Hybrids functionality must be tested again to make sure there aren't
      // any issues, with the global form state, when two or more hybrids are added for the same field.
      return <ReviewPlan {...parentRowData} />;
    }, [multipleHybrids]);

    const columns = useMemo(
      () => [
        {
          header: getMessage('bulk-fvss-table-column_field'),
          size: 150,
          minSize: 150,
          maxSize: 150,
          cell: ({ row, table }) => {
            const rows = table.getRowModel().rows;
            const firstIndexWithSameId = rows.findIndex(
              (r) => r.original.id === row.original.id
            );
            const isFirstWithSameId = row.index === firstIndexWithSameId;
            const lastIndexWithSameId = rows.reduce(
              (lastIdx, r, idx) => (r.original.id === row.original.id ? idx : lastIdx),
              -1
            );
            const isLastWithSameId = row.index === lastIndexWithSameId;

            return (
              <span className={!isLastWithSameId && multipleHybrids ? 'extra-field-hybrid' : ''}>
                {isFirstWithSameId ? row.original.name : ''}
              </span>
            );
          },
        },
        {
          header: getMessage('bulk-fvss-review-scripts-hybrid-table-header'),
          size: 200,
          cell: ({ row, table }) => {
            const rows = table.getRowModel().rows;
            const lastIndexWithSameId = rows.reduce(
              (lastIdx, r, idx) => (r.original.id === row.original.id ? idx : lastIdx),
              -1
            );
            // TODO: Remove the false statement when the multiple hibrids functionality is enabled
            // behind a flag for advisors. Multiple Hybrids functionality must be tested again to make sure there aren't
            // any issues, with the global form state, when two or more hybrids are added for the same field.
            const showHybridButton = false && row.index === lastIndexWithSameId && multipleHybrids;
            const isExpanded = row.getIsExpanded();

            return (
              <>
                <HybridDropdownCell
                  control={control}
                  setValue={setValue}
                  getValues={getValues}
                  update={update}
                  name={`fields.${row.index}.hybrid`}
                  usedHybrids={usedHybrids}
                  setUsedHybrids={setUsedHybrids}
                  fieldCountryCode={row.original.countryCode}
                  rowIndex={row.index}
                  disabled={isExpanded && !row.original.recommendation}
                  excludedBrandUuids={excludedBrandUuids}
                  onRecommendationClear={() => {
                    if (isExpanded) {
                      row.toggleExpanded();
                    }
                  }}
                />
                {showHybridButton && (
                  <Button
                    testId="add-more-hybrids-trigger"
                    fill={ButtonFill.Ghost}
                    size={ComponentSize.Sm}
                    onClick={() => {
                      insert(row.index + 1, {
                        ...row.original,
                        recommendation: undefined,
                        hybrid: undefined,
                        avgFixedPopulation: undefined,
                        seedCostPerBag: undefined,
                        targetYield: undefined,
                        selectedRecommendation: undefined,
                      });
                    }}
                  >
                    <Icon glyph={Icon.set.ADD} />
                    {getMessage('corn-product')}
                  </Button>
                )}
              </>
            );
          },
        },
        {
          header: getMessage(
            'bulk-fvss-review-scripts-avg-fixed-population-table-header',
            { unit: inferUnit('seed-density').symbol.quantitySpecific }
          ),
          size: 200,
          cell: ({ row }) => {
            const isExpanded = row.getIsExpanded();

            return (
              <PopulationInputCell
                control={control}
                name={`fields.${row.index}.avgFixedPopulation`}
                rowIndex={row.index}
                showTooltip={isExpanded && !!row.original.recommendation}
                disabled={isExpanded}
              />
            );
          },
        },
        {
          header:  `${getMessage('price-product')} (${getMessage('per-unit', { unit: getMessage('bag-unit') })})`,
          size: 180,
          cell: ({ row }) => {
            const isExpanded = row.getIsExpanded();

            return (
              <SeedCostInputCell
                control={control}
                name={`fields.${row.index}.seedCostPerBag`}
                currency={currency}
                bagSize={bagSize}
                rowIndex={row.index}
                disabled={isExpanded}
                showTooltip={isExpanded && !!row.original.recommendation}
              />
            );
          },
        },
        {
          header: `${getMessage('target-yield')} (${inferUnit('area-density.yield').symbol.quantitySpecific})`,
          size: 180,
          cell: ({ row }) => {
            const isExpanded = row.getIsExpanded();

            return (
              <TargetYieldInputCell
                control={control}
                name={`fields.${row.index}.targetYield`}
                currency={currency}
                rowIndex={row.index}
                fieldUuid={row.original.id}
                disabled={isExpanded}
                showTooltip={isExpanded && !!row.original.recommendation}
              />
            );
          },
        },
        {
          id: 'actions',
          header: () => (
            <GenerateAllButton
              onGenerate={() => {
                generateHandlersRef.current.forEach(handler => handler());
              }}
            />
          ),
          minSize: 75,
          cell: ({ row }) => {
            if (row.original.recommendation) {
              if (!row.getIsExpanded()) {
                row.toggleExpanded();
              }
            }

            const onExpandRow = (force?: boolean) => {
              if (!row.getIsExpanded() || force) {
                row.toggleExpanded();
              }
            };

            return (
              <ActionsCell
                bagSize={bagSize}
                fieldData={row.original}
                rowIndex={row.index}
                onRemove={() => handleRemove(row.index)}
                onExpandRow={onExpandRow}
                update={update}
                generateHandlersRef={generateHandlersRef}
              />
            );
          },
        },
      ],
      [
        usedHybrids,
        currency,
        bagSize,
        multipleHybrids,
        insert,
        update,
        control,
        setValue,
        getValues,
        handleRemove,
      ]
    );

    const table = useReactTable({
      data: fieldsWithIndex,
      columns,
      state: { expanded },
      getRowId: (row) => row._id,
      getCoreRowModel: getCoreRowModel(),
      onExpandedChange: setExpanded,
      getExpandedRowModel: getExpandedRowModel(),
    });

    useEffect(() => {
      if (fieldsWithIndex.length > 0) {
        onFieldsLoad(fieldsWithIndex);
      }
    }, []);

    return (
      <>
        <ErrorHandler />
        <Table
          table={table}
          isReady={true}
          showPagination={false}
          expandedRowComponent={renderReviewPlan}
          emptyTableContent={{ message: '' }}
        />
      </>
    );
  }
);
